/**
 * FieldMappingRow Component
 *
 * Single field mapping row for join condition building.
 * Supports both field picker and formula expression modes.
 *
 * @module FieldMappingRow
 */

import { X, FunctionSquare } from 'lucide-react';
import type { TableFieldApiResponse } from '@loader/shared-types';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@loader/ui';

import { MonacoFormulaEditor } from '@/features/linkedTables/editLinkedTable/formula/MonacoFormulaEditor';
import type { SchemaColumn } from '@/features/linkedTables/editLinkedTable/formula/operatorConstants';
import { FieldCombobox } from '@/features/linkedTables/editLinkedTable/components/FieldCombobox';
import type { FieldMapping } from '@/lib/types';
import { cn } from '@/lib/utils';

interface FieldMappingRowProps {
  mapping: FieldMapping;
  index: number;
  sourceFields: TableFieldApiResponse[];
  targetFields: TableFieldApiResponse[];
  sourceTableName: string;
  targetTableName: string;
  sourceSchema: SchemaColumn[];
  targetSchema: SchemaColumn[];
  onUpdate: (index: number, mapping: FieldMapping) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

export function FieldMappingRow({
  mapping,
  index,
  sourceFields,
  targetFields,
  sourceTableName,
  targetTableName,
  sourceSchema,
  targetSchema,
  onUpdate,
  onRemove,
  canRemove,
}: FieldMappingRowProps) {
  const isFormulaMode = mapping.mode === 'formula';

  // Determine the selected field's data type on each side for cross-filtering
  const selectedSourceType = sourceFields.find((f) => f.id === mapping.source_field_id)?.data_type;
  const selectedTargetType = targetFields.find((f) => f.id === mapping.target_field_id)?.data_type;

  const toggleMode = () => {
    onUpdate(index, {
      ...mapping,
      mode: isFormulaMode ? 'field' : 'formula',
    });
  };

  return (
    <div className="flex items-center gap-2">
      {/* Formula mode toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={toggleMode}
            aria-label={isFormulaMode ? 'Switch to field mode' : 'Switch to formula mode'}
          >
            <FunctionSquare className={cn('h-4 w-4', isFormulaMode ? 'text-primary' : 'text-muted-foreground')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left">
          {isFormulaMode ? 'Switch to field picker' : 'Use formula expression'}
        </TooltipContent>
      </Tooltip>

      {isFormulaMode ? (
        <>
          {/* Source formula */}
          <div className="min-w-0 basis-[42%]">
            <MonacoFormulaEditor
              value={mapping.source_formula || ''}
              onChange={(value) =>
                onUpdate(index, { ...mapping, source_formula: value })
              }
              schema={sourceSchema}
              height="32px"
              compact
            />
          </div>

          {/* Equals sign */}
          <span className="text-muted-foreground shrink-0">=</span>

          {/* Target formula */}
          <div className="min-w-0 basis-[42%]">
            <MonacoFormulaEditor
              value={mapping.target_formula || ''}
              onChange={(value) =>
                onUpdate(index, { ...mapping, target_formula: value })
              }
              schema={targetSchema}
              height="32px"
              compact
            />
          </div>
        </>
      ) : (
        <>
          {/* Source field */}
          <div className="min-w-0 basis-[42%]">
            <FieldCombobox
              fields={sourceFields}
              value={mapping.source_field_id}
              onValueChange={(value) =>
                onUpdate(index, { ...mapping, source_field_id: value })
              }
              tableName={sourceTableName}
              filterDataType={selectedTargetType}
            />
          </div>

          {/* Equals sign */}
          <span className="text-muted-foreground shrink-0">=</span>

          {/* Target field */}
          <div className="min-w-0 basis-[42%]">
            <FieldCombobox
              fields={targetFields}
              value={mapping.target_field_id}
              onValueChange={(value) =>
                onUpdate(index, { ...mapping, target_field_id: value })
              }
              tableName={targetTableName}
              filterDataType={selectedSourceType}
            />
          </div>
        </>
      )}

      {/* Remove button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={() => onRemove(index)}
        disabled={!canRemove}
        aria-label="Remove field mapping"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
